import type { DriverShipment } from "@khazzin/api-client";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Button } from "@khazzin/ui/primitives";
import { useCallback, useEffect, useState } from "react";
import { isDone } from "../../app/shell/stops";
import { useI18n } from "../../i18n";
import { del, get, put } from "../../offline/db";
import { dropPhoto, keepPhoto, MEDIA_REF, photoOf } from "../../offline/media";
import { type Day, useCommand, useOffline } from "../../offline/Offline";
import { PodStep } from "../pod/PodStep";
import { emptyPod, type PodDraft, podPayload } from "../pod/pod";
import { FailedSheet } from "./FailedSheet";
import {
	acceptedLines,
	allKept,
	canStart,
	type Draft,
	emptyFailed,
	type FailedDraft,
} from "./outcome";
import { PartialPicker } from "./PartialPicker";

/**
 * A stop's outcome actions (DA-10-17, EP10-S17): **Delivered** (with
 * **Partial**, a delivery of some lines) and **Failed** — the driver's only
 * status changes (owner, ADJ-1022-8b) — and **Done** once it is recorded,
 * with **Queued** until the server has it. Every action is a queued command,
 * so the stop is done the moment the driver submits, signal or not.
 *
 * There is no Start delivery button any more: an outcome on a parcel still on
 * the van queues `start_delivery` first, so the sync replays the move out and
 * then the outcome, in order, with no signal as with it.
 *
 * Delivered, and a partial's Continue, open the proof of delivery (EP10-S18):
 * Complete delivery queues `attach_pod` and then `record_attempt` under one
 * key, the order the server needs (the delivery's guard reads the POD).
 *
 * The capture in progress is checkpointed in IndexedDB (`drafts`) at every
 * step and its photos and signature in `media`, so a phone killed mid-capture
 * reopens on the same stop with the same reason, lines and proof.
 */

/** The cached day with one stop's status changed: the optimistic half of a command. */
function withStatus(id: string, status: string) {
	return (d: Day): Day => {
		const s = d.shipments[id];
		return s === undefined ? d : { ...d, shipments: { ...d.shipments, [id]: { ...s, status } } };
	};
}

/** Where the phone is, or null when it cannot say within ten seconds. */
function whereAmI(): Promise<{ lat: number; lng: number; accuracyM: number } | null> {
	return new Promise((resolve) => {
		if (!("geolocation" in navigator)) {
			resolve(null);
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(p) =>
				resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: p.coords.accuracy }),
			() => resolve(null),
			{ enableHighAccuracy: true, timeout: 10_000 },
		);
	});
}

/** A draft's files on the phone, for the screens. */
interface Files {
	photo: Blob | null;
	photos: Blob[];
	signature: Blob | null;
}

const NO_FILES: Files = { photo: null, photos: [], signature: null };

async function filesOf(d: Draft | undefined): Promise<Files> {
	if (d?.kind === "failed" && d.photoId !== undefined) {
		return { ...NO_FILES, photo: await photoOf(d.photoId) };
	}
	if (d?.kind === "pod") {
		const photos = await Promise.all(d.photoIds.map(photoOf));
		return {
			...NO_FILES,
			photos: photos.filter((p): p is Blob => p !== null),
			signature: d.signatureId === undefined ? null : await photoOf(d.signatureId),
		};
	}
	return NO_FILES;
}

function mediaOf(d: Draft | null): string[] {
	if (d?.kind === "failed") {
		return d.photoId === undefined ? [] : [d.photoId];
	}
	if (d?.kind === "pod") {
		return [...d.photoIds, ...(d.signatureId === undefined ? [] : [d.signatureId])];
	}
	return [];
}

export function OutcomeActions({ stop }: { stop: DriverShipment }) {
	const { t } = useI18n();
	const { outbox } = useOffline();
	const start = useCommand("start_delivery");
	const record = useCommand("record_attempt");
	const attach = useCommand("attach_pod");
	const id = stop.shipmentId;
	const key = `outcome:${id}`;
	const [draft, setDraft] = useState<Draft | null>(null);
	const [files, setFiles] = useState<Files>(NO_FILES);

	/** A parcel still on the van goes out first, queued ahead of its outcome. */
	async function takeOut() {
		if (canStart(stop.status)) {
			await start({}, { shipmentId: id, apply: withStatus(id, "delivering") });
		}
	}

	// Reopen a capture the phone was killed in.
	useEffect(() => {
		let live = true;
		void (async () => {
			const saved = await get<Draft>("drafts", key);
			const taken = await filesOf(saved);
			if (live) {
				setDraft(saved ?? null);
				setFiles(taken);
			}
		})();
		return () => {
			live = false;
		};
	}, [key]);

	const save = useCallback(
		(next: Draft | null) => {
			setDraft(next);
			void (next === null ? del("drafts", key) : put("drafts", key, next));
		},
		[key],
	);

	function cancel() {
		for (const media of mediaOf(draft)) {
			void dropPhoto(media);
		}
		setFiles(NO_FILES);
		save(null);
	}

	async function takePhoto(failed: FailedDraft, taken: Blob) {
		const photoId = crypto.randomUUID();
		await keepPhoto(photoId, id, taken);
		if (failed.photoId !== undefined) {
			void dropPhoto(failed.photoId);
		}
		setFiles({ ...NO_FILES, photo: taken });
		save({ ...failed, photoId });
	}

	async function addPodPhoto(pod: PodDraft, taken: Blob) {
		const photoId = crypto.randomUUID();
		await keepPhoto(photoId, id, taken);
		setFiles((f) => ({ ...f, photos: [...f.photos, taken] }));
		save({ ...pod, photoIds: [...pod.photoIds, photoId] });
	}

	function removePodPhoto(pod: PodDraft, index: number) {
		const photoId = pod.photoIds[index];
		if (photoId !== undefined) {
			void dropPhoto(photoId);
		}
		setFiles((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== index) }));
		save({ ...pod, photoIds: pod.photoIds.filter((_, i) => i !== index) });
	}

	async function setSignature(pod: PodDraft, png: Blob | null) {
		if (pod.signatureId !== undefined) {
			void dropPhoto(pod.signatureId);
		}
		const { signatureId: _, ...rest } = pod;
		if (png === null) {
			setFiles((f) => ({ ...f, signature: null }));
			save(rest);
			return;
		}
		const signatureId = crypto.randomUUID();
		await keepPhoto(signatureId, id, png, "signature");
		setFiles((f) => ({ ...f, signature: png }));
		save({ ...rest, signatureId });
	}

	async function submitFailed(failed: FailedDraft): Promise<string | null> {
		if (failed.photoId === undefined) {
			return "driver.outcome.photoRequired";
		}
		const geo = await whereAmI();
		const note = failed.note.trim();
		await takeOut();
		await record(
			{
				outcome: "failed",
				failureReason: failed.reason,
				occurredAt: new Date().toISOString(),
				...(note === "" ? {} : { note }),
				...(failed.reason === "customer_postponed" ? { requestedDate: failed.requestedDate } : {}),
				...(geo === null ? {} : { geo }),
				proofRef: `${MEDIA_REF}${failed.photoId}`,
			},
			{ shipmentId: id, apply: withStatus(id, "delivery_failed") },
		);
		// The photo stays in `media` until its upload: the attempt names it.
		setFiles(NO_FILES);
		save(null);
		return null;
	}

	/** The proof, then the delivery it closes, under one key. */
	async function completePod(pod: PodDraft) {
		const attemptKey = crypto.randomUUID();
		const now = new Date().toISOString();
		const geo = await whereAmI();
		await takeOut();
		await attach(podPayload(pod, attemptKey, now), { shipmentId: id });
		const partial = pod.outcome === "partial";
		await record(
			{
				clientEventKey: attemptKey,
				outcome: pod.outcome,
				occurredAt: now,
				...(partial ? { acceptedLines: acceptedLines(stop.lines ?? [], pod.kept ?? {}) } : {}),
				...(geo === null ? {} : { geo }),
			},
			{ shipmentId: id, apply: withStatus(id, partial ? "partially_delivered" : "delivered") },
		);
		// The files stay in `media` until their upload: the proof names them.
		setFiles(NO_FILES);
		save(null);
	}

	const queued = outbox.some((e) => e.shipmentId === id && e.state === "queued");

	if (isDone(stop.status)) {
		return (
			<div className="flex flex-wrap gap-1.5">
				<StatusChip tone="success" size="sm">
					{t("driver.outcome.done")}
				</StatusChip>
				{queued && (
					<StatusChip tone="neutral" size="sm">
						{t("driver.outcome.queued")}
					</StatusChip>
				)}
			</div>
		);
	}
	if (stop.status !== "delivering" && !canStart(stop.status)) {
		// Not handed over yet: nothing to do at the door.
		return null;
	}

	return (
		<section aria-label={t("driver.outcome.actions")} className="flex flex-col gap-2 card p-4">
			<Button variant="primary" size="lg" onClick={() => save(emptyPod("success"))}>
				{t("driver.outcome.delivered")}
			</Button>
			<div className="grid grid-cols-2 gap-2">
				<Button
					size="lg"
					onClick={() => save({ kind: "partial", kept: allKept(stop.lines ?? []) })}
				>
					{t("driver.outcome.partial")}
				</Button>
				<Button variant="danger" size="lg" onClick={() => save(emptyFailed())}>
					{t("driver.outcome.failed")}
				</Button>
			</div>
			{draft?.kind === "failed" && (
				<FailedSheet
					draft={draft}
					photo={files.photo}
					onChange={save}
					onPhoto={(taken) => void takePhoto(draft, taken)}
					onCancel={cancel}
					onSubmit={() => submitFailed(draft)}
				/>
			)}
			{draft?.kind === "partial" && (
				<PartialPicker
					stop={stop}
					kept={draft.kept}
					onChange={(kept) => save({ kind: "partial", kept })}
					onCancel={cancel}
					onContinue={() => save(emptyPod("partial", draft.kept))}
				/>
			)}
			{draft?.kind === "pod" && (
				<PodStep
					stop={stop}
					draft={draft}
					photos={files.photos}
					signature={files.signature}
					onChange={save}
					onPhoto={(taken) => void addPodPhoto(draft, taken)}
					onRemovePhoto={(index) => removePodPhoto(draft, index)}
					onSignature={(png) => void setSignature(draft, png)}
					onCancel={cancel}
					onComplete={() => completePod(draft)}
				/>
			)}
		</section>
	);
}
